import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { isApiRequestError } from "@/utils/apiRequestError";
import { presentMutationError } from "@/utils/showApiErrorToast";
import {
  applyCoachEntryPatchToSlots,
  buildCoachComposerPayload,
  buildCoachScanSummary,
  buildCoachTurnRequest,
  flattenCoachPatchOps,
  hasUsableCoachOhlcStats,
  inferCoachTurnGoal,
  isCoachQuotaSlug,
  isCoachThreadNotFoundSlug,
  parseCoachComposerSlots,
} from "@/utils/strategyRdCoachContext";
import {
  deriveStrategyRdCoachLabStage,
  isStrategyRdCoachLabActionChip,
  STRATEGY_RD_COACH_LAB_ACTION_IDS,
  strategyRdCoachChipsExceptAsked,
  strategyRdCoachChipsForTurn,
  strategyRdCoachMessageForBacktest,
  strategyRdCoachOpenedBacktestChips,
  strategyRdCoachOpenedBacktestLabel,
  type StrategyRdCoachOpenedBacktest,
} from "@/utils/strategyRdCoachLabStage";
import {
  createStrategyRdCoachThread,
  deleteStrategyRdCoachThread,
  getStrategyRdCoachOrientation,
  getStrategyRdCoachOrientationFaq,
  getStrategyRdCoachThread,
  getStrategyRdCoachActivity,
  getStrategyRdCoachUsage,
  getStrategyRdCoachWowSeed,
  strategyRdCoachTurnTrace,
  listStrategyRdCoachStyles,
  listStrategyRdCoachThreads,
  patchStrategyRdCoachThread,
  postStrategyRdCoachIntake,
  postStrategyRdCoachThreadMessages,
  postStrategyRdCoachTurn,
  validateStrategyRdCoachPatch,
  parseStrategyRdCoachLabActions,
} from "@/services/strategyRdCoachApi";
import {
  getStrategyRdResultCard,
  pollStrategyRdJob,
} from "@/services/strategyRdApi";
import { STRATEGY_RD_API_BASE } from "@/types/strategyRd";
import {
  readStrategyRdCoachAttachment,
  strategyRdCoachImagesThatFit,
  type StrategyRdCoachAttachment,
} from "@/utils/strategyRdCoachImages";
import type {
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerEntrySlot,
  StrategyRdComposerSetupSlot,
  StrategyRdLogicPath,
  StrategyRdOhlcStats,
  StrategyRdOutcomeSummary,
} from "@/types/strategyRd";
import type {
  StrategyRdCoachActivity,
  StrategyRdCoachChip,
  StrategyRdCoachComposerPatch,
  StrategyRdCoachFocus,
  StrategyRdCoachLabAction,
  StrategyRdCoachProposal,
  StrategyRdCoachOrientationFaq,
  StrategyRdCoachOrientationResponse,
  StrategyRdCoachStyle,
  StrategyRdCoachThreadSummary,
  StrategyRdCoachTurnResponse,
  StrategyRdCoachUiMessage,
  StrategyRdCoachUsage,
} from "@/types/strategyRdCoach";
import {
  clampStrategyRdCoachWidth,
  defaultStrategyRdCoachWidth,
  readStrategyRdCoachWidth,
  writeStrategyRdCoachWidth,
} from "./strategyRdCoachWidth";

export type StrategyRdCoachLabApply = {
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  candleType?: string | null;
  sessionIds?: string[] | null;
  packId?: string | null;
  lookbackDays?: number | null;
  /** N of M (`set_optional_min`). */
  optionalMin?: number | null;
};

/** What every turn sends about the Lab (§10) — read at send time so unsaved edits go too. */
export type StrategyRdCoachLabState = {
  hypothesisId: string | null;
  template: Record<string, unknown> | null;
  sizing: Record<string, unknown> | null;
};

/** "Ask TES about this" — a candle (or a trade's entry candle) on the Lab chart. */
export type StrategyRdCoachChartFocus = { time: string; candle_type?: string | null };

const REVIEW_BACKTEST_MESSAGE = "Review the backtest you ran";

/** Shown once TES's running backtest finishes; the review is a turn only when the user asks. */
const REVIEW_BACKTEST_CHIP: StrategyRdCoachChip = {
  id: STRATEGY_RD_COACH_LAB_ACTION_IDS.reviewBacktest,
  label: "Review the backtest",
};
const LAB_ACTION_MAX_WAIT_MS = 20 * 60 * 1000;

export type UseStrategyRdCoachArgs = {
  enabled: boolean;
  /** Open the panel when the Lab market window is confirmed. Bootstrap can run earlier. */
  autoOpen?: boolean;
  symbol: string;
  candleType: string;
  sessionIds: string[];
  lookbackDays: number;
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  ohlcStats?: StrategyRdOhlcStats | null;
  logicPath?: StrategyRdLogicPath | null;
  outcomeSummary?: StrategyRdOutcomeSummary | null;
  previewCommitted?: boolean;
  hasScanned?: boolean;
  matchCount?: number | null;
  onApplyComposer: (next: StrategyRdCoachLabApply) => void;
  onAfterComposerApplied?: () => void;
  /** Lab next-step: run Preview on the canvas. */
  onLabRunPreview?: () => void;
  /** Lab next-step: expand composer to confluences / entry. */
  onLabFocusComposer?: (section: "confluences" | "entry" | "setup") => void;
  /** Chips "See your recipe" / "See your backtest settings". */
  onLabOpenRecipe?: () => void;
  onLabOpenBacktestSettings?: () => void;
  /** Actions on a backtest opened from history: its Versions tab, and the save dialog. */
  onLabCompareVersions?: () => void;
  onLabSaveStrategy?: () => void;
  /** Sent as `market` so TES can backtest a recipe that isn't saved yet. */
  assetClass?: string | null;
  getLabState?: () => StrategyRdCoachLabState;
  /** Which side of the Lab the chat sits on (resize drags the opposite edge). */
  dock?: "left" | "right";
  /** Confirm card: apply TES's template / sizing proposals (after its composer change). */
  onApplyProposal?: (proposal: {
    template?: Record<string, unknown> | null;
    template_patch?: Record<string, unknown> | null;
    sizing?: Record<string, unknown> | null;
    sizing_patch?: Record<string, unknown> | null;
  }) => void;
  /** A TES backtest / search / robustness job finished (refresh lists, etc.). */
  onLabActionFinished?: (action: StrategyRdCoachLabAction) => void;
};

function newMessageId(): string {
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const COACH_MINIMIZED_KEY = "strategyRd.coach.minimized";

function readCoachMinimized(): boolean {
  try {
    return window.localStorage.getItem(COACH_MINIMIZED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCoachMinimized(minimized: boolean): void {
  try {
    window.localStorage.setItem(COACH_MINIMIZED_KEY, minimized ? "1" : "0");
  } catch {
    // Storage blocked — the choice lasts until reload.
  }
}

export function useStrategyRdCoach(args: UseStrategyRdCoachArgs) {
  const {
    enabled,
    autoOpen = false,
    symbol,
    candleType,
    sessionIds,
    lookbackDays,
    setup,
    confluences,
    entry,
    ohlcStats,
    logicPath,
    outcomeSummary,
    previewCommitted = false,
    hasScanned = false,
    matchCount = null,
    onApplyComposer,
    onAfterComposerApplied,
    getLabState,
    dock = "right",
    onApplyProposal,
    onLabActionFinished,
    onLabRunPreview,
    onLabFocusComposer,
    onLabOpenRecipe,
    onLabOpenBacktestSettings,
    onLabCompareVersions,
    onLabSaveStrategy,
    assetClass = null,
  } = args;

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(() =>
    typeof window !== "undefined" ? readStrategyRdCoachWidth(window.innerWidth) : 420
  );
  const [bootstrapped, setBootstrapped] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [usage, setUsage] = useState<StrategyRdCoachUsage | null>(null);
  const [styles, setStyles] = useState<StrategyRdCoachStyle[]>([]);
  const [threads, setThreads] = useState<StrategyRdCoachThreadSummary[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // A backtest opened from history that has no chat yet: the empty chat is about it, and the
  // thread is only created on the first message (browsing history shouldn't fill Recents).
  const [backtestChat, setBacktestChat] = useState<{ id: string; name: string } | null>(null);
  const backtestChatRef = useRef<{ id: string; name: string } | null>(null);
  backtestChatRef.current = backtestChat;
  const backtestOpenSeq = useRef(0);
  // A backtest opened from history: the chat scrolls to the turn that ran it and offers
  // actions on it, until the next message or chat switch.
  const [openedBacktest, setOpenedBacktest] = useState<StrategyRdCoachOpenedBacktest | null>(null);
  const [messages, setMessages] = useState<StrategyRdCoachUiMessage[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [suggestedChips, setSuggestedChips] = useState<StrategyRdCoachChip[]>([]);
  const [focus, setFocus] = useState<StrategyRdCoachFocus | null>(null);
  const [disclaimer, setDisclaimer] = useState<string | null>(null);
  /** Orientation "Start a strategy" (`cta.type: start_intake`) shows the style pills. */
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [orientationFaqs, setOrientationFaqs] = useState<StrategyRdCoachOrientationFaq[]>(
    []
  );
  const [orientationChips, setOrientationChips] = useState<StrategyRdCoachChip[]>([]);
  /** The latest reply's proposals — one Confirm card applies all three or none. */
  const [pendingProposal, setPendingProposal] = useState<StrategyRdCoachProposal | null>(null);
  const [attachments, setAttachments] = useState<StrategyRdCoachAttachment[]>([]);
  const [chartFocus, setChartFocus] = useState<StrategyRdCoachChartFocus | null>(null);
  const [input, setInput] = useState("");
  const [goal, setGoal] = useState<string>("build");
  const [sending, setSending] = useState(false);
  /** What TES is doing right now (polled while a turn is in flight). */
  const [activity, setActivity] = useState<StrategyRdCoachActivity | null>(null);
  const activityRef = useRef<StrategyRdCoachActivity | null>(null);
  const activityReadAtRef = useRef(0);
  useEffect(() => {
    if (!sending) {
      setActivity(null);
      return;
    }
    activityRef.current = null;
    let stop = false;
    const startedAt = Date.now() / 1000;
    const tick = async () => {
      try {
        const next = await getStrategyRdCoachActivity();
        if (stop) return;
        // Ignore the previous turn's list until this turn has started on the server.
        if (next.started != null && next.started < startedAt - 5) return;
        activityRef.current = next;
        activityReadAtRef.current = Date.now();
        setActivity(next);
      } catch {
        /* the reply still arrives without the live list */
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 900);
    return () => {
      stop = true;
      window.clearInterval(id);
    };
  }, [sending]);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [confirmingPatch, setConfirmingPatch] = useState(false);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [threadsBusy, setThreadsBusy] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<StrategyRdCoachLabApply | null>(
    null
  );

  const hostWidthRef = useRef(0);
  const dockRef = useRef(dock);
  dockRef.current = dock;
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const bootstrapOnceRef = useRef(false);

  const labStageFocus = useCallback((): StrategyRdCoachFocus => {
    const stage = deriveStrategyRdCoachLabStage({
      setup,
      confluences,
      entry,
      previewCommitted,
      hasScanned,
    });
    const confluenceCount = (confluences || []).filter((c) =>
      Boolean(String(c?.id || "").trim())
    ).length;
    return {
      ...(focus || {}),
      lab_stage: stage,
      preview_committed: previewCommitted,
      confluence_count: confluenceCount,
      match_count: matchCount,
    };
  }, [confluences, entry, focus, hasScanned, matchCount, previewCommitted, setup]);

  const openPanel = useCallback(() => {
    writeCoachMinimized(false);
    setPanelOpen(true);
  }, []);

  /** Fold for now (a drawer needs the room): not remembered, unlike minimizing. */
  const foldPanel = useCallback(() => setPanelOpen(false), []);

  const minimizePanel = useCallback(() => {
    // Remembered: a minimized TES stays minimized on the next visit / reload.
    writeCoachMinimized(true);
    setPanelOpen(false);
  }, []);

  const setHostWidth = useCallback((w: number) => {
    hostWidthRef.current = w;
    setPanelWidth((prev) => clampStrategyRdCoachWidth(prev || defaultStrategyRdCoachWidth(w), w));
  }, []);

  const onResizeStart = useCallback(
    (clientX: number) => {
      dragRef.current = { startX: clientX, startWidth: panelWidth };
    },
    [panelWidth]
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current) return;
      const host = hostWidthRef.current || window.innerWidth;
      const moved = e.clientX - dragRef.current.startX;
      const delta = dockRef.current === "left" ? moved : -moved;
      const next = clampStrategyRdCoachWidth(
        dragRef.current.startWidth + delta,
        host
      );
      setPanelWidth(next);
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setPanelWidth((w) => {
        writeStrategyRdCoachWidth(w);
        return w;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const refreshThreads = useCallback(async (signal?: AbortSignal) => {
    const list = await listStrategyRdCoachThreads(30, signal);
    setThreads(list.threads);
    return list.threads;
  }, []);

  const applyOrientationPack = useCallback((orientationRes: StrategyRdCoachOrientationResponse) => {
    setOrientationFaqs(orientationRes.faqs);
    setOrientationChips(
      [...orientationRes.starter_chips].sort((a, b) => {
        if (a.id === "where_do_i_start") return -1;
        if (b.id === "where_do_i_start") return 1;
        return 0;
      })
    );
  }, []);

  const bootstrap = useCallback(async () => {
    if (!enabled || bootstrapped || bootstrapping) return;
    setBootstrapping(true);
    try {
      const [usageRes, stylesRes, threadList, orientationRes] = await Promise.all([
        getStrategyRdCoachUsage(),
        // Styles are optional chips: a failure there must not hide the user's credits / chats.
        listStrategyRdCoachStyles().catch(() => ({ styles: [] as Awaited<ReturnType<typeof listStrategyRdCoachStyles>>["styles"] })),
        listStrategyRdCoachThreads(30),
        getStrategyRdCoachOrientation().catch(() => null),
      ]);
      setUsage(usageRes);
      setQuotaBlocked(usageRes.turns_remaining <= 0);
      setDisclaimer(usageRes.disclaimer || orientationRes?.disclaimer || null);
      setStyles(stylesRes.styles);
      if (orientationRes) applyOrientationPack(orientationRes);
      setThreads(threadList.threads);
      let active = sessionId;
      // A backtest opened before TES finished loading has already picked its chat.
      // The Lab opens on a new chat: reuse an empty one, else the first send makes one
      // (ensureSession). The last chat with messages is offered as "Pick up where you left off".
      if (!active && !backtestChatRef.current) {
        const empty = threadList.threads.find((t) => (t.message_count ?? 0) === 0);
        if (empty) {
          active = empty.id;
          setSessionId((prev) => prev ?? active);
        }
      }
      setBootstrapped(true);
    } catch (err) {
      presentMutationError(err, "Could not open TES");
    } finally {
      setBootstrapping(false);
    }
  }, [applyOrientationPack, bootstrapped, bootstrapping, enabled, sessionId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const about = backtestChatRef.current;
    const created = await createStrategyRdCoachThread(about?.name ?? null, undefined, {
      hypothesisId: about?.id,
    });
    setSessionId(created.id);
    setBacktestChat(null);
    setThreads((prev) => [created, ...prev.filter((t) => t.id !== created.id)]);
    return created.id;
  }, [sessionId]);

  const appendOrientationFaq = useCallback(
    (faq: StrategyRdCoachOrientationFaq) => {
      const userMsg: StrategyRdCoachUiMessage = {
        id: newMessageId(),
        role: "user",
        content: faq.label,
      };
      const mapped =
        faq.suggested_next
          ?.map((id) => {
            const row = orientationFaqs.find((f) => f.id === id);
            return row
              ? ({ id: row.id, label: row.label, faq_id: row.id } as StrategyRdCoachChip)
              : null;
          })
          .filter((c): c is StrategyRdCoachChip => Boolean(c)) || [];
      const nextChips = strategyRdCoachChipsExceptAsked(mapped, [
        ...messages.filter((m) => m.role === "user").map((m) => m.content),
        faq.label,
      ]);
      const assistant: StrategyRdCoachUiMessage = {
        id: newMessageId(),
        role: "assistant",
        content: faq.answer_markdown,
        suggested_chips: nextChips.length > 0 ? nextChips : null,
        source: "orientation",
      };
      setMessages((prev) => [...prev, userMsg, assistant]);
      if (faq.cta?.type === "start_intake") {
        setIntakeOpen(true);
      }
      void (async () => {
        try {
          const threadId = await ensureSession();
          await postStrategyRdCoachThreadMessages(threadId, {
            messages: [
              { role: "user", content: faq.label, meta: { faq_id: faq.id } },
              {
                role: "assistant",
                content: faq.answer_markdown,
                meta: {
                  faq_id: faq.id,
                  ...(nextChips.length > 0 ? { suggested_chips: nextChips } : {}),
                },
              },
            ],
          });
          const pack = await getStrategyRdCoachOrientation(undefined, threadId);
          applyOrientationPack(pack);
        } catch (err) {
          presentMutationError(err, "Could not save this help answer");
        }
      })();
    },
    [applyOrientationPack, ensureSession, messages, orientationFaqs]
  );

  const onOrientationChip = useCallback(
    async (chip: StrategyRdCoachChip) => {
      const faqId = chip.faq_id || chip.id;
      const local = orientationFaqs.find((f) => f.id === faqId);
      if (local) {
        appendOrientationFaq(local);
        return;
      }
      try {
        const faq = await getStrategyRdCoachOrientationFaq(faqId, undefined, sessionId);
        setOrientationFaqs((prev) =>
          prev.some((f) => f.id === faq.id) ? prev : [...prev, faq]
        );
        appendOrientationFaq(faq);
      } catch (err) {
        presentMutationError(err, "Could not load help");
      }
    },
    [appendOrientationFaq, orientationFaqs, sessionId]
  );


  useEffect(() => {
    if (!enabled) {
      bootstrapOnceRef.current = false;
      setPanelOpen(false);
      return;
    }
    if (!bootstrapOnceRef.current) {
      bootstrapOnceRef.current = true;
      void bootstrap();
    }
  }, [enabled, bootstrap]);

  useEffect(() => {
    if (autoOpen && !readCoachMinimized()) setPanelOpen(true);
  }, [autoOpen]);

  useEffect(() => {
    if (!enabled || !bootstrapped || !sessionId) return;
    let cancelled = false;
    const controller = new AbortController();
    void getStrategyRdCoachOrientation(controller.signal, sessionId)
      .then((pack) => {
        if (!cancelled) applyOrientationPack(pack);
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [applyOrientationPack, bootstrapped, enabled, sessionId]);

  const applyIntakeComposer = useCallback(
    (intake: {
      composer?: Parameters<typeof parseCoachComposerSlots>[0];
      pack_id?: string | null;
      style_id?: string | null;
      suggested_chips?: StrategyRdCoachChip[] | null;
      lookbackDays?: number | null;
      candleType?: string | null;
      sessionIds?: string[] | null;
    }) => {
      const slots = parseCoachComposerSlots(intake.composer);
      if (slots) {
        onApplyComposer({
          ...slots,
          packId: intake.pack_id || null,
          lookbackDays: intake.lookbackDays ?? null,
          candleType: intake.candleType ?? slots.candleType ?? null,
          sessionIds: intake.sessionIds ?? slots.sessionIds ?? null,
        });
      } else if (intake.candleType || intake.lookbackDays || intake.sessionIds) {
        onApplyComposer({
          setup,
          confluences,
          entry,
          candleType: intake.candleType ?? null,
          sessionIds: intake.sessionIds ?? null,
          lookbackDays: intake.lookbackDays ?? null,
          packId: intake.pack_id || null,
        });
      }
      setFocus((prev) => ({
        ...(prev || {}),
        style_id: intake.style_id || prev?.style_id || null,
        pack_id: intake.pack_id || prev?.pack_id || null,
      }));
      if (intake.suggested_chips?.length) {
        setSuggestedChips(intake.suggested_chips);
      }
    },
    [confluences, entry, onApplyComposer, setup]
  );

  const runIntake = useCallback(
    async (opts: { styleId?: string | null; packId?: string | null; goal?: string }) => {
      setIntakeBusy(true);
      try {
        const res = await postStrategyRdCoachIntake({
          style_id: opts.styleId || undefined,
          pack_id: opts.packId || undefined,
          symbol: symbol || undefined,
          candle_type: candleType || undefined,
          session_ids: sessionIds.length ? sessionIds : undefined,
          goal: opts.goal || goal || "build",
        });
        applyIntakeComposer(res);
        if (res.goal) setGoal(res.goal);
        setIntakeOpen(false);
        toast.success("Composer updated from style");
      } catch (err) {
        presentMutationError(err, "Intake failed");
      } finally {
        setIntakeBusy(false);
      }
    },
    [applyIntakeComposer, candleType, goal, sessionIds, symbol]
  );

  const runWowSeed = useCallback(async () => {
    setIntakeBusy(true);
    try {
      const wow = await getStrategyRdCoachWowSeed(symbol || undefined);
      if (wow.intake) {
        applyIntakeComposer({
          ...wow.intake,
          lookbackDays: wow.window?.lookback_days ?? lookbackDays,
        });
      }
      if (wow.opening_chips?.length) setSuggestedChips(wow.opening_chips);
      if (wow.style_id) {
        setFocus((prev) => ({ ...(prev || {}), style_id: wow.style_id }));
      }
      toast.success("Wow seed loaded — run Preview when ready");
      onAfterComposerApplied?.();
    } catch (err) {
      presentMutationError(err, "Wow seed failed");
    } finally {
      setIntakeBusy(false);
    }
  }, [applyIntakeComposer, lookbackDays, onAfterComposerApplied, symbol]);

  const sendTurn = useCallback(
    async (
      text?: string,
      goalOverride?: string,
      opts?: { hypothesisId?: string | null; auto?: boolean }
    ) => {
      const message = (text ?? input).trim();
      if (!message || sending || quotaBlocked) return;
      const auto = opts?.auto === true;
      const lab = getLabState?.() ?? null;
      const turnImages = auto ? [] : attachments;
      const turnChartFocus = auto ? null : chartFocus;
      const turnGoal =
        goalOverride || inferCoachTurnGoal(message, goal || "build");
      if (turnGoal !== goal) setGoal(turnGoal);
      setSending(true);
      const userMsg: StrategyRdCoachUiMessage = {
        id: newMessageId(),
        role: "user",
        content: message,
        ...(turnImages.length > 0 ? { image_previews: turnImages.map((a) => a.preview) } : {}),
      };
      setMessages((prev) => [...prev, userMsg]);
      if (!auto) setInput("");
      setSuggestedChips([]);
      setOpenedBacktest(null);

      const composer = buildCoachComposerPayload({
        candleType,
        setup,
        confluences,
        entry,
        sessionIds,
      });
      const scanSummary = buildCoachScanSummary({
        ohlcStats: ohlcStats || null,
        logicPath: logicPath || null,
      });
      const hasFunnelStats = hasUsableCoachOhlcStats(ohlcStats);

      // why_no_hits needs real Preview/Scan funnel numbers — don't burn a turn empty-handed.
      if (turnGoal === "why_no_hits" && !hasFunnelStats) {
        const assistant: StrategyRdCoachUiMessage = {
          id: newMessageId(),
          role: "assistant",
          content:
            "The chart hasn't finished finding setups yet, so there are no match numbers to explain.\n\n" +
            "Give it a moment to load, then ask again. " +
            "I'll walk setup → confluence → path → matches using the real numbers.",
        };
        setMessages((prev) => [...prev, assistant]);
        setSending(false);
        return;
      }

      const attempt = async (sid: string) => {
        const body = buildCoachTurnRequest({
          message,
          goal: turnGoal,
          composer,
          scanSummary,
          outcomeSummary: outcomeSummary || null,
          history: [...messages, userMsg],
          sessionId: sid,
          focus: labStageFocus(),
          hypothesisId: opts?.hypothesisId ?? lab?.hypothesisId ?? null,
          template: lab?.template ?? null,
          sizing: lab?.sizing ?? null,
          images: turnImages.map((a) => ({ mime_type: a.mime_type, data_base64: a.data_base64 })),
          chartFocus: turnChartFocus,
          market: symbol.trim()
            ? {
                symbol: symbol.trim(),
                candle_type: candleType,
                lookback_days: lookbackDays,
                asset_class: assetClass?.trim() || null,
              }
            : null,
        });
        return postStrategyRdCoachTurn(body);
      };

      try {
        let sid = await ensureSession();
        let res: StrategyRdCoachTurnResponse;
        try {
          res = await attempt(sid);
        } catch (err) {
          if (
            isApiRequestError(err) &&
            isCoachThreadNotFoundSlug(err.slug)
          ) {
            const created = await createStrategyRdCoachThread();
            sid = created.id;
            setSessionId(sid);
            setThreads((prev) => [created, ...prev]);
            res = await attempt(sid);
          } else {
            throw err;
          }
        }

        const usageUpdate = res.usage;
        if (usageUpdate) {
          setUsage((prev) =>
            prev
              ? {
                  ...prev,
                  ...usageUpdate,
                  turns_remaining:
                    usageUpdate.turns_remaining ?? prev.turns_remaining,
                  tokens_remaining:
                    usageUpdate.tokens_remaining ?? prev.tokens_remaining,
                  soft_warn: usageUpdate.soft_warn ?? prev.soft_warn,
                }
              : (usageUpdate as StrategyRdCoachUsage)
          );
          if (
            usageUpdate.turns_remaining != null &&
            usageUpdate.turns_remaining <= 0
          ) {
            setQuotaBlocked(true);
          }
        }

        const reply =
          (res.reply_markdown || "").trim() ||
          "TES returned an empty answer. Try rephrasing, or run a Preview and ask again.";
        const proposal: StrategyRdCoachProposal | null =
          res.composer_patch?.ops?.length ||
          res.template ||
          res.template_patch ||
          res.sizing ||
          res.sizing_patch
            ? {
                composer_patch: res.composer_patch?.ops?.length ? res.composer_patch : null,
                template: res.template ?? null,
                template_patch: res.template_patch ?? null,
                sizing: res.sizing ?? null,
                sizing_patch: res.sizing_patch ?? null,
                patch_readback: res.patch_readback ?? null,
              }
            : null;
        const lastActivity = activityRef.current;
        const trace =
          lastActivity && lastActivity.steps.length > 0
            ? strategyRdCoachTurnTrace(
                lastActivity,
                (lastActivity.now ?? Date.now() / 1000) + (Date.now() - activityReadAtRef.current) / 1000
              )
            : null;
        const assistant: StrategyRdCoachUiMessage = {
          id: newMessageId(),
          role: "assistant",
          content: reply,
          activity: trace,
          suggested_chips: strategyRdCoachChipsForTurn(res.suggested_chips),
          composer_patch: res.composer_patch || null,
          source: res.source || null,
          proposal,
          lab_actions: res.lab_actions ?? null,
        };
        // Jobs TES left running: when they all finish, TES reviews them (one follow-up turn).
        const running = (res.lab_actions ?? []).filter(
          (a) => a.status === "queued" || a.status === "running"
        );
        if (running.length > 0) {
          followUpWatchRef.current.set(assistant.id, new Set(running.map((a) => a.job_id)));
        }
        setMessages((prev) => [...prev, assistant]);
        setPendingProposal(proposal);
        // Jobs that finished inside the turn never reach the poller: land them now too.
        for (const a of res.lab_actions ?? []) {
          if (a.status === "ready" || a.status === "failed") onLabActionFinished?.(a);
        }
        if (!auto) {
          setAttachments([]);
          setChartFocus(null);
        }
        if (res.disclaimer) setDisclaimer(res.disclaimer);
        void refreshThreads();
      } catch (err) {
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
        if (!auto) setInput(message);
        if (isApiRequestError(err) && isCoachQuotaSlug(err.slug)) {
          setQuotaBlocked(true);
          toast.error("Weekly TES quota reached", {
            description: err.action || "Upgrade or wait until the week rolls.",
          });
        } else if (
          isApiRequestError(err) &&
          err.slug === "strategy_rd.coach.invalid_image"
        ) {
          toast.error("Couldn't send the image", { description: err.message });
        } else if (
          isApiRequestError(err) &&
          err.slug === "strategy_rd.coach.tes_unavailable"
        ) {
          toast.error("TES temporarily unavailable", {
            description: "Try style chips or retry in a moment.",
          });
        } else {
          presentMutationError(err, "TES turn failed");
        }
      } finally {
        setSending(false);
      }
    },
    [
      assetClass,
      candleType,
      confluences,
      ensureSession,
      entry,
      goal,
      input,
      labStageFocus,
      logicPath,
      lookbackDays,
      symbol,
      messages,
      onLabActionFinished,
      attachments,
      chartFocus,
      getLabState,
      ohlcStats,
      outcomeSummary,
      quotaBlocked,
      refreshThreads,
      sending,
      sessionIds,
      setup,
    ]
  );

  /**
   * "Try a style" / a style chip: the same TES turn as typing "Build me a … strategy", so it
   * shows TES working and builds and backtests the strategy. Out of turns: the free style seed.
   */
  const buildFromStyle = useCallback(
    (styleId: string) => {
      const style = styles.find((s) => s.id === styleId);
      if (!style || quotaBlocked) {
        void runIntake({ styleId });
        return;
      }
      void sendTurn(`Build me a ${style.name} strategy`, "build");
    },
    [quotaBlocked, runIntake, sendTurn, styles]
  );

  /** "Surprise me": TES picks a style and market, then builds it in the chat like any other ask. */
  const surpriseMe = useCallback(async () => {
    if (quotaBlocked) {
      await runWowSeed();
      return;
    }
    let message = "Surprise me: build me a strategy to start from";
    setIntakeBusy(true);
    try {
      const wow = await getStrategyRdCoachWowSeed(symbol || undefined);
      const style = styles.find((s) => s.id === wow.style_id);
      const market = wow.symbol || symbol;
      if (style) message = `Surprise me: build me a ${style.name} strategy${market ? ` on ${market}` : ""}`;
    } catch {
      // No seed: TES still picks one itself.
    } finally {
      setIntakeBusy(false);
    }
    void sendTurn(message, "build");
  }, [quotaBlocked, runWowSeed, sendTurn, styles, symbol]);

  const resolveProposal = useCallback((how: "confirmed" | "dismissed") => {
    setMessages((prev) =>
      prev.map((m) =>
        m.proposal && !m.proposal.resolved ? { ...m, proposal: { ...m.proposal, resolved: how } } : m
      )
    );
    setPendingProposal(null);
  }, []);

  /** Confirm applies the composer, template and sizing proposals together; nothing on failure. */
  const confirmPatch = useCallback(async () => {
    const proposal = pendingProposal;
    if (!proposal) return;
    setConfirmingPatch(true);
    try {
      let slots: StrategyRdCoachLabApply | null = null;
      if (proposal.composer_patch?.ops?.length) {
        const composer = buildCoachComposerPayload({
          candleType,
          setup,
          confluences,
          entry,
          sessionIds,
        });
        const patch = flattenCoachPatchOps(proposal.composer_patch);
        const validated = await validateStrategyRdCoachPatch({
          composer,
          composer_patch: patch,
        });
        if (!validated.ok || !validated.composer) {
          toast.error("Invalid patch", {
            description: validated.error || "Nothing was changed.",
          });
          resolveProposal("dismissed");
          return;
        }
        const parsed = parseCoachComposerSlots(validated.composer);
        slots = parsed ? applyCoachEntryPatchToSlots(parsed, patch) : null;
      }
      if (slots) {
        onApplyComposer({ ...slots, packId: focus?.pack_id || null });
      }
      // Rules: prefer full merged template/sizing from the turn; else deep-merge patches.
      // Never invent template.entry from composer.entry.mode alone (see applyCoachComposer).
      if (
        proposal.template ||
        proposal.template_patch ||
        proposal.sizing ||
        proposal.sizing_patch
      ) {
        onApplyProposal?.({
          template: proposal.template ?? null,
          template_patch: proposal.template_patch ?? null,
          sizing: proposal.sizing ?? null,
          sizing_patch: proposal.sizing_patch ?? null,
        });
      }
      if (slots) onAfterComposerApplied?.();
      resolveProposal("confirmed");
      toast.success("Lab updated");
    } catch (err) {
      if (
        isApiRequestError(err) &&
        (err.slug === "strategy_rd.coach.unknown_concept" ||
          err.slug === "strategy_rd.coach.invalid_patch")
      ) {
        toast.error("Could not apply patch", {
          description: err.message || "Stay on catalog chips.",
        });
        setPendingProposal(null);
      } else {
        presentMutationError(err, "Validate patch failed");
      }
    } finally {
      setConfirmingPatch(false);
    }
  }, [
    candleType,
    confluences,
    entry,
    focus?.pack_id,
    onAfterComposerApplied,
    onApplyComposer,
    onApplyProposal,
    pendingProposal,
    resolveProposal,
    sessionIds,
    setup,
  ]);

  const dismissPatch = useCallback(() => {
    resolveProposal("dismissed");
  }, [resolveProposal]);

  // --- TES lab actions: poll each job, then show its card ------------------------------
  const pollingJobsRef = useRef(new Set<string>());
  const pollAbortRef = useRef(new Set<AbortController>());
  /** Assistant message id → job ids TES left running (a "Review the backtest" chip when all finish). */
  const followUpWatchRef = useRef(new Map<string, Set<string>>());
  const followUpHypothesisRef = useRef(new Map<string, string>());
  /** The backtest the "Review the backtest" chip reviews. */
  const reviewHypothesisRef = useRef<string | null>(null);

  const updateLabAction = useCallback(
    (messageId: string, jobId: string, patch: Partial<StrategyRdCoachLabAction>) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId && m.lab_actions
            ? {
                ...m,
                lab_actions: m.lab_actions.map((a) => (a.job_id === jobId ? { ...a, ...patch } : a)),
              }
            : m
        )
      );
    },
    []
  );

  useEffect(
    () => () => {
      pollAbortRef.current.forEach((c) => c.abort());
    },
    []
  );

  useEffect(() => {
    for (const m of messages) {
      for (const action of m.lab_actions ?? []) {
        if (action.status !== "queued" && action.status !== "running") continue;
        if (pollingJobsRef.current.has(action.job_id)) continue;
        pollingJobsRef.current.add(action.job_id);
        const controller = new AbortController();
        pollAbortRef.current.add(controller);
        const messageId = m.id;
        void (async () => {
          let finished: StrategyRdCoachLabAction = { ...action };
          try {
            const result = await pollStrategyRdJob(
              action.poll_url || `${STRATEGY_RD_API_BASE}/jobs/${encodeURIComponent(action.job_id)}`,
              action.kind,
              {
                signal: controller.signal,
                maxWaitMs: LAB_ACTION_MAX_WAIT_MS,
                onProgress: (p) =>
                  updateLabAction(messageId, action.job_id, {
                    status: p.status === "backfilling" ? "running" : p.status,
                    message: p.message,
                    progress: p.stage ?? null,
                  }),
              }
            );
            const hid =
              (typeof result.hypothesis_id === "string" && result.hypothesis_id) || action.hypothesis_id || null;
            const outcome = typeof result.outcome === "string" ? result.outcome : null;
            const card =
              hid && outcome !== "no_setups"
                ? await getStrategyRdResultCard(hid, controller.signal).catch(() => null)
                : null;
            finished = {
              ...action,
              status: "ready",
              hypothesis_id: hid,
              outcome,
              card,
              progress: null,
              message: typeof result.message === "string" ? result.message : null,
            };
          } catch (err) {
            if (err instanceof DOMException && err.name === "AbortError") return;
            finished = {
              ...action,
              status: "failed",
              progress: null,
              message: err instanceof Error ? err.message : "The job failed.",
            };
          }
          updateLabAction(messageId, action.job_id, finished);
          onLabActionFinished?.(finished);
          void getStrategyRdCoachUsage()
            .then((u) => setUsage(u))
            .catch(() => undefined);
          const watch = followUpWatchRef.current.get(messageId);
          if (watch) {
            watch.delete(action.job_id);
            if (finished.status === "ready" && finished.hypothesis_id) {
              followUpHypothesisRef.current.set(messageId, finished.hypothesis_id);
            }
            if (watch.size === 0) {
              followUpWatchRef.current.delete(messageId);
              const hypothesisId = followUpHypothesisRef.current.get(messageId);
              followUpHypothesisRef.current.delete(messageId);
              // Don't start a turn on the user's behalf: offer the review as the first chip.
              if (hypothesisId) {
                reviewHypothesisRef.current = hypothesisId;
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? {
                          ...msg,
                          suggested_chips: [
                            REVIEW_BACKTEST_CHIP,
                            ...(msg.suggested_chips ?? []).filter((c) => c.id !== REVIEW_BACKTEST_CHIP.id),
                          ],
                        }
                      : msg
                  )
                );
              }
            }
          }
        })();
      }
    }
  }, [messages, onLabActionFinished, updateLabAction]);

  // --- Screenshots and "Ask TES about this" --------------------------------------------
  const addAttachments = useCallback(
    async (files: File[]) => {
      const { accepted, error } = strategyRdCoachImagesThatFit(
        attachments,
        files.map((f) => ({ type: f.type, size: f.size }))
      );
      if (error) toast.error(error);
      try {
        const read = await Promise.all(accepted.map((i) => readStrategyRdCoachAttachment(files[i])));
        setAttachments((prev) => [...prev, ...read]);
      } catch (err) {
        presentMutationError(err, "Could not read the image");
      }
    },
    [attachments]
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  /** Opens the chat pre-filled; `chart_focus` goes with the next message. */
  const askAbout = useCallback((focusAt: StrategyRdCoachChartFocus, kind: "candle" | "trade") => {
    setPanelOpen(true);
    setChartFocus(focusAt);
    setInput((prev) =>
      prev.trim() ? prev : kind === "trade" ? "Explain this trade." : "What's happening here?"
    );
  }, []);

  const loadThread = useCallback(
    /** `keepLabRecipe`: the Lab already shows the recipe (a backtest was opened), so don't
     *  offer to swap in the one this chat last saved. */
    async (id: string, opts?: { keepLabRecipe?: boolean }): Promise<StrategyRdCoachUiMessage[] | null> => {
      setThreadsBusy(true);
      setOpenedBacktest(null);
      try {
        const detail = await getStrategyRdCoachThread(id);
        setSessionId(detail.id);
        setBacktestChat(null);
        const loaded: StrategyRdCoachUiMessage[] = detail.messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({
            id: m.id || newMessageId(),
            role: m.role as "user" | "assistant",
            content: m.content,
            suggested_chips:
              (m.meta?.suggested_chips as StrategyRdCoachChip[] | undefined) ||
              null,
            composer_patch:
              (m.meta?.composer_patch as StrategyRdCoachComposerPatch | undefined) ||
              null,
            lab_actions: parseStrategyRdCoachLabActions(m.meta?.lab_actions),
          }));
        setMessages(loaded);
        setPendingProposal(null);
        setPendingRestore(null);
        const snap = detail.lab_snapshot;
        if (snap?.composer && !opts?.keepLabRecipe) {
          const slots = parseCoachComposerSlots(snap.composer);
          if (slots) {
            setPendingRestore({
              ...slots,
              packId: snap.pack_id || null,
            });
          }
        }
        if (snap?.focus) setFocus(snap.focus);
        else if (snap?.style_id || snap?.pack_id) {
          setFocus({
            style_id: snap.style_id,
            pack_id: snap.pack_id,
          });
        }
        if (snap?.goal) setGoal(String(snap.goal));
        return loaded;
      } catch (err) {
        presentMutationError(err, "Could not load chat");
        return null;
      } finally {
        setThreadsBusy(false);
      }
    },
    []
  );

  const confirmRestore = useCallback(() => {
    if (!pendingRestore) return;
    onApplyComposer(pendingRestore);
    setPendingRestore(null);
    toast.success("Loaded this chat's recipe");
  }, [onApplyComposer, pendingRestore]);

  const dismissRestore = useCallback(() => {
    setPendingRestore(null);
  }, []);

  const newThread = useCallback(async () => {
    setThreadsBusy(true);
    try {
      const created = await createStrategyRdCoachThread();
      setSessionId(created.id);
      setBacktestChat(null);
      setOpenedBacktest(null);
      setMessages([]);
      setPendingProposal(null);
      setSuggestedChips([]);
      setPendingRestore(null);
      setThreads((prev) => [created, ...prev]);
    } catch (err) {
      if (
        isApiRequestError(err) &&
        err.slug === "strategy_rd.coach.thread_limit"
      ) {
        toast.error("Chat limit reached", {
          description: "Delete an old chat to start a new one.",
        });
      } else {
        presentMutationError(err, "Could not create chat");
      }
    } finally {
      setThreadsBusy(false);
    }
  }, []);

  const removeThread = useCallback(
    async (id: string) => {
      setThreadsBusy(true);
      try {
        await deleteStrategyRdCoachThread(id);
        setThreads((prev) => prev.filter((t) => t.id !== id));
        if (sessionId === id) {
          setSessionId(null);
          setMessages([]);
          setPendingProposal(null);
          await newThread();
        }
      } catch (err) {
        presentMutationError(err, "Could not delete chat");
      } finally {
        setThreadsBusy(false);
      }
    },
    [newThread, sessionId]
  );

  /** A backtest opened from history: open TES on the chat that ran it (newest), scrolled to
   *  that run, else an empty chat about it — with actions on it either way. `stayIfUnlinked`:
   *  a version of the open strategy with no chat of its own keeps the current chat. */
  const openChatForBacktest = useCallback(
    async (
      hypothesisId: string,
      name?: string | null,
      opts?: { stayIfUnlinked?: boolean; version?: number | null; saved?: boolean }
    ) => {
      if (!enabled || !hypothesisId) return;
      openPanel();
      const opened = (list: StrategyRdCoachUiMessage[]) =>
        setOpenedBacktest({
          hypothesisId,
          name: name?.trim() || "this backtest",
          version: opts?.version ?? null,
          saved: Boolean(opts?.saved),
          messageId: strategyRdCoachMessageForBacktest(list, hypothesisId),
          token: ++backtestOpenSeq.current,
        });
      const active = threads.find((t) => t.id === sessionId);
      if (active?.hypothesis_ids?.includes(hypothesisId)) {
        setBacktestChat(null);
        opened(messagesRef.current);
        return;
      }
      const seq = ++backtestOpenSeq.current;
      try {
        const { threads: linked } = await listStrategyRdCoachThreads(1, undefined, { hypothesisId });
        if (seq !== backtestOpenSeq.current) return;
        if (linked[0]) {
          const loaded = await loadThread(linked[0].id, { keepLabRecipe: true });
          if (loaded && seq === backtestOpenSeq.current) opened(loaded);
          return;
        }
      } catch {
        // The backtest itself opened; fall through to an empty chat about it.
        if (seq !== backtestOpenSeq.current) return;
      }
      if (opts?.stayIfUnlinked && (sessionId || backtestChatRef.current)) {
        opened([]);
        return;
      }
      setSessionId(null);
      setBacktestChat({ id: hypothesisId, name: name?.trim() || "this backtest" });
      setMessages([]);
      setPendingProposal(null);
      setSuggestedChips([]);
      setPendingRestore(null);
      opened([]);
    },
    [enabled, loadThread, openPanel, sessionId, threads]
  );

  /** Actions on the backtest opened from history, in place of the last turn's chips. */
  const openedBacktestChips = useMemo(
    () => (openedBacktest ? strategyRdCoachOpenedBacktestChips(openedBacktest) : []),
    [openedBacktest]
  );

  /** A backtest TES ran in this chat finished: link it, so reopening it brings this chat back. */
  const linkBacktestToChat = useCallback(
    async (hypothesisId: string) => {
      if (!sessionId || !hypothesisId) return;
      const active = threads.find((t) => t.id === sessionId);
      if (active?.hypothesis_ids?.includes(hypothesisId)) return;
      try {
        const updated = await patchStrategyRdCoachThread(sessionId, {
          lab_snapshot: { hypothesis_id: hypothesisId },
        });
        setThreads((prev) =>
          prev.map((t) => (t.id === updated.id ? { ...t, hypothesis_ids: updated.hypothesis_ids } : t))
        );
      } catch {
        // Not worth a toast: the next turn links it too.
      }
    },
    [sessionId, threads]
  );

  const onChipClick = useCallback(
    (chip: StrategyRdCoachChip) => {
      // Clear chip rows so the same suggestions don't render twice (message + footer).
      setSuggestedChips([]);
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant" && last.suggested_chips?.length) {
          next[next.length - 1] = { ...last, suggested_chips: null };
        }
        return next;
      });

      if (isStrategyRdCoachLabActionChip(chip)) {
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.runPreview) {
          onLabRunPreview?.();
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.addConfluences) {
          onLabFocusComposer?.("confluences");
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.configureEntry) {
          onLabFocusComposer?.("entry");
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.tuneSetupWindow) {
          onLabFocusComposer?.("setup");
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.openRecipe) {
          onLabOpenRecipe?.();
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.openBacktestSettings) {
          onLabOpenBacktestSettings?.();
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.explainEntry) {
          void sendTurn(
            "How should Entry work for this setup — signal close vs break, and how that maps to longs and shorts?",
            "explain"
          );
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.explainSides) {
          void sendTurn(
            "Confirm my setup settings: when does this take longs vs shorts, and what do polarity and trade side mean here?",
            "explain"
          );
          return;
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.reviewBacktest) {
          void sendTurn(REVIEW_BACKTEST_MESSAGE, "review_backtest", {
            hypothesisId: reviewHypothesisRef.current,
          });
          return;
        }
        if (openedBacktest) {
          const label = strategyRdCoachOpenedBacktestLabel(openedBacktest);
          const about = { hypothesisId: openedBacktest.hypothesisId };
          if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.reviewOpenedBacktest) {
            void sendTurn(`Review ${label}: what's working, what isn't, and what you'd change next. Just the review — don't run anything yet.`, "review_backtest", about);
            return;
          }
          if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.tryVariation) {
            void sendTurn(`Suggest the one change to ${label} most likely to improve it, and backtest it.`, "improve", about);
            return;
          }
          if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.compareVersions) {
            onLabCompareVersions?.();
            return;
          }
          if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.saveStrategy) {
            onLabSaveStrategy?.();
            return;
          }
        }
        if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.askWhyNoHits) {
          void sendTurn("Why no matches on this Preview?", "why_no_hits");
          return;
        }
        return;
      }

      if (chip.id.startsWith("lab_")) return;

      if (chip.faq_id || orientationFaqs.some((f) => f.id === chip.id)) {
        void onOrientationChip(chip);
        return;
      }
      if (chip.pack_id || chip.id.startsWith("pack:")) {
        const packId = chip.pack_id || chip.id.replace(/^pack:/, "");
        void runIntake({ packId, goal: chip.goal || goal });
        return;
      }
      if (chip.style_id) {
        buildFromStyle(chip.style_id);
        return;
      }
      const knownGoals = new Set([
        "build",
        "why_no_hits",
        "improve",
        "explain_matches",
        "what_else",
        "explain",
        "explain_setup",
        "why_zero_matches",
        "explain_zero_hits",
        "loosen_confluences",
        "try_alt_pack",
        "run_apply",
      ]);
      let nextGoal = goal;
      if (chip.goal && knownGoals.has(chip.goal)) {
        nextGoal =
          chip.goal === "why_zero_matches" || chip.goal === "explain_zero_hits"
            ? "why_no_hits"
            : chip.goal;
        setGoal(nextGoal);
      } else if (knownGoals.has(chip.id)) {
        nextGoal =
          chip.id === "why_zero_matches" || chip.id === "explain_zero_hits"
            ? "why_no_hits"
            : chip.id;
        setGoal(nextGoal);
      }
      const isActionChip =
        chip.id.startsWith("loosen_") ||
        chip.id.startsWith("try_") ||
        chip.id === "run_apply" ||
        chip.id === "try_alt_pack";
      if (!isActionChip && chip.label.trim()) {
        void sendTurn(chip.label.trim(), nextGoal);
        return;
      }
      setInput(chip.label);
    },
    [
      buildFromStyle,
      goal,
      onLabFocusComposer,
      onLabCompareVersions,
      onLabOpenBacktestSettings,
      onLabOpenRecipe,
      onLabRunPreview,
      onLabSaveStrategy,
      openedBacktest,
      onOrientationChip,
      orientationFaqs,
      runIntake,
      sendTurn,
    ]
  );

  return {
    activity,
    panelOpen,
    openPanel,
    foldPanel,
    minimizePanel,
    panelWidth,
    setHostWidth,
    onResizeStart,
    bootstrapping,
    usage,
    styles,
    threads,
    sessionId,
    messages,
    suggestedChips,
    pendingProposal,
    attachments,
    addAttachments,
    removeAttachment,
    chartFocus,
    clearChartFocus: () => setChartFocus(null),
    askAbout,
    input,
    setInput,
    goal,
    setGoal,
    sending,
    intakeBusy,
    confirmingPatch,
    quotaBlocked,
    threadsBusy,
    pendingRestore,
    focus,
    runIntake,
    runWowSeed,
    buildFromStyle,
    surpriseMe,
    sendTurn,
    confirmPatch,
    dismissPatch,
    loadThread,
    newThread,
    removeThread,
    backtestChat,
    openedBacktest,
    openedBacktestChips,
    openChatForBacktest,
    linkBacktestToChat,
    confirmRestore,
    dismissRestore,
    onChipClick,
    disclaimer,
    orientationChips,
    onOrientationChip,
    intakeOpen,
    closeIntake: () => setIntakeOpen(false),
  };
}
